import DriverAddParams, {
  DriverAddParamsAddress,
  DriverAddParamsTaxiDriverAssociation,
  DriverAddParamsTaxiDriverAssociationForB2B,
  DriverAddParamsTaxiDriverAssociationForEmployment,
} from "../../../../../../common/services/driver/add/driver-add-params";
import AddressType from "../../../../../../common/types/address-type";
import UserCitizenship from "../../../../../../common/types/user-citizenship";
import UserFormOfEmployment from "../../../../../../common/types/user-form-of-employment";
import UserLanguage from "../../../../../../common/types/user-language";
import UserVehicleOwnership from "../../../../../../common/types/user-vehicle-ownership-form";
import dateService from "../../../../../../common/utils/date/date.service";
import DriverAddAccountFormData from "../form/types/driver-add-account-form-data";
import DriverAddTaxiCorporationFormData from "../form/types/driver-add-taxi-corporation-form-data";
import DriverAddVehicleFormData from "../form/types/driver-add-vehicle-form-data";
import RailyDriverAddUserFormData from "../form/types/raily-driver-add-user-form-data";
import DriverAddAddress from "../types/driver-add-address";
import DriverAddAddressType from "../types/driver-add-address-type";
import DriverAddCarOwnershipType from "../types/driver-add-car-ownership-type";
import DriverAddCitizenshipType from "../types/driver-add-citizenship-type";
import DriverAddFormOfEmploymentType from "../types/driver-add-form-of-employment-type";
import DriverAddLanguageType from "../types/driver-add-language-type";

const driverAddParamsType = (type: DriverAddAddressType): AddressType => {
  switch (type) {
    case DriverAddAddressType.MAILING:
      return AddressType.MAILING;
    case DriverAddAddressType.STARTING:
      return AddressType.STARTING;
  }
};

const driverAddParamsContractType = (
  type: DriverAddFormOfEmploymentType | null,
): UserFormOfEmployment | undefined => {
  switch (type) {
    case DriverAddFormOfEmploymentType.B2B:
      return UserFormOfEmployment.B2B;
    case DriverAddFormOfEmploymentType.EMPLOYMENT:
      return UserFormOfEmployment.EMPLOYMENT;
    default:
      return undefined;
  }
};

const driverAddParamsCarOwnershipType = (
  type: DriverAddCarOwnershipType,
): UserVehicleOwnership => {
  switch (type) {
    case DriverAddCarOwnershipType.BUSINESS:
      return UserVehicleOwnership.BUSINESS;
    case DriverAddCarOwnershipType.OWNER:
      return UserVehicleOwnership.OWNER;
  }
};

const driverAddParamsCitizenshipType = (
  type: DriverAddCitizenshipType,
): UserCitizenship => {
  switch (type) {
    case DriverAddCitizenshipType.BY:
      return UserCitizenship.BY;
    case DriverAddCitizenshipType.CZ:
      return UserCitizenship.CZ;
    case DriverAddCitizenshipType.DE:
      return UserCitizenship.DE;
    case DriverAddCitizenshipType.LT:
      return UserCitizenship.LT;
    case DriverAddCitizenshipType.PL:
      return UserCitizenship.PL;
    case DriverAddCitizenshipType.RU:
      return UserCitizenship.RU;
    case DriverAddCitizenshipType.SK:
      return UserCitizenship.SK;
    case DriverAddCitizenshipType.UA:
      return UserCitizenship.UA;
  }
};

const driverAddParamsLanguageType = (
  type: DriverAddLanguageType,
): UserLanguage => {
  switch (type) {
    case DriverAddLanguageType.BY:
      return UserLanguage.BY;
    case DriverAddLanguageType.CZ:
      return UserLanguage.CZ;
    case DriverAddLanguageType.DE:
      return UserLanguage.DE;
    case DriverAddLanguageType.LT:
      return UserLanguage.LT;
    case DriverAddLanguageType.PL:
      return UserLanguage.PL;
    case DriverAddLanguageType.RU:
      return UserLanguage.RU;
    case DriverAddLanguageType.SK:
      return UserLanguage.SK;
    case DriverAddLanguageType.UA:
      return UserLanguage.UA;
    case DriverAddLanguageType.EN:
      return UserLanguage.EN;
    case DriverAddLanguageType.ES:
      return UserLanguage.ES;
    case DriverAddLanguageType.FR:
      return UserLanguage.FR;
  }
};

const driverAddParamsLanguagesType = (
  types: DriverAddLanguageType[],
): UserLanguage[] => {
  return types.map(driverAddParamsLanguageType);
};

const createDriverAddParamsAddress = (
  userDataAddress: DriverAddAddress,
): DriverAddParamsAddress => {
  const paramsAddress: DriverAddParamsAddress = {
    apartment: userDataAddress.apartmentNumber.trim(),
    country: userDataAddress.country.trim(),
    description: !!userDataAddress.description
      ? userDataAddress.description.trim()
      : null,
    houseNo: userDataAddress.houseNumber.trim(),
    lat: userDataAddress.latitude,
    lon: userDataAddress.longitude,
    street: userDataAddress.street.trim(),
    town: userDataAddress.town.trim(),
    type: driverAddParamsType(userDataAddress.type),
    zipCode: userDataAddress.zipCode.trim(),
  };

  return paramsAddress;
};

const createDriverAddParamsAddresses = (
  userDataAddresses: DriverAddAddress[],
): DriverAddParamsAddress[] => {
  return userDataAddresses.map(createDriverAddParamsAddress);
};

const createDriverAddParamsTaxiDriverAssociationForEmployment = (
  formUserFormData: RailyDriverAddUserFormData,
  formTaxiCorporationData: DriverAddTaxiCorporationFormData,
): DriverAddParamsTaxiDriverAssociationForEmployment => {
  return {
    contractType: driverAddParamsContractType(
      formTaxiCorporationData.formOfEmployment,
    )!,
    taxiCorporationId: formTaxiCorporationData.taxiCorporation?.uuid
      ? formTaxiCorporationData.taxiCorporation?.uuid
      : "",
    displayName: `${formUserFormData.firstName} ${formUserFormData.lastName}`,
    fleetPartnerId: formTaxiCorporationData.fleetPartner?.uuid ?? null,
  };
};

const createDriverAddParamsTaxiDriverAssociationForB2B = (
  formUserFormData: RailyDriverAddUserFormData,
  formTaxiCorporationData: DriverAddTaxiCorporationFormData,
): DriverAddParamsTaxiDriverAssociationForB2B => {
  return {
    apartment: formTaxiCorporationData.apartmentNumber.trim(),
    companyName: formTaxiCorporationData.name.trim(),
    contractType: driverAddParamsContractType(
      formTaxiCorporationData.formOfEmployment,
    )!,
    fleetPartnerId: formTaxiCorporationData.fleetPartner?.uuid ?? null,
    country: formTaxiCorporationData.country.trim(),
    description: formTaxiCorporationData.description.trim(),
    houseNo: formTaxiCorporationData.houseNumber.trim(),
    regon: formTaxiCorporationData.companyId.trim() || null,
    taxId: formTaxiCorporationData.taxNumber.trim(),
    zipCode: formTaxiCorporationData.zipCode.trim(),
    town: formTaxiCorporationData.town.trim(),
    krsNo: formTaxiCorporationData.nationalCourtRegisterNumber.trim() || null,
    street: formTaxiCorporationData.street.trim(),
    taxiCorporationId: formTaxiCorporationData.taxiCorporation?.uuid
      ? formTaxiCorporationData.taxiCorporation?.uuid
      : "",
    displayName: `${formUserFormData.firstName} ${formUserFormData.lastName}`,
  };
};

const createDriverAddParamsTaxiDriverAssociation = (
  formTaxiCorporationData: DriverAddTaxiCorporationFormData,
  formUserFormData: RailyDriverAddUserFormData,
): DriverAddParamsTaxiDriverAssociation => {
  if (
    formTaxiCorporationData.formOfEmployment ===
    DriverAddFormOfEmploymentType.B2B
  ) {
    return createDriverAddParamsTaxiDriverAssociationForB2B(
      formUserFormData,
      formTaxiCorporationData,
    );
  }

  return createDriverAddParamsTaxiDriverAssociationForEmployment(
    formUserFormData,
    formTaxiCorporationData,
  );
};

const createSubmitFormParams = (
  formAccountFormData: DriverAddAccountFormData,
  formUserFormData: RailyDriverAddUserFormData,
  formVehicleFormData: DriverAddVehicleFormData,
  formTaxiCorporationData: DriverAddTaxiCorporationFormData[],
): DriverAddParams => {
  return {
    email: formAccountFormData.email.trim(),
    username: formAccountFormData.username.trim(),
    password: formAccountFormData.password.trim(),
    firstName: formUserFormData.firstName.trim(),
    lastName: formUserFormData.lastName.trim(),
    mobilePrimaryPrefix: formUserFormData.mobile.dialingCode!.trim(),
    mobilePrimary: formUserFormData.mobile.number!.trim(),
    mobileSecondaryPrefix:
      formUserFormData.alternativeMobile.dialingCode.trim() || null,
    mobileSecondary: formUserFormData.alternativeMobile.number.trim() || null,
    driver: {
      birthDate: dateService.format(formUserFormData.birthDate!, "yyyy-mm-dd"),
      birthPlace: formUserFormData.birthPlace.trim(),
      carMake: formVehicleFormData.make.trim(),
      carModel: formVehicleFormData.model.trim(),
      carOwnership: driverAddParamsCarOwnershipType(
        formVehicleFormData.ownership!,
      ),
      carProdYear: formVehicleFormData.productionYear!,
      carRegNo: formVehicleFormData.registrationNumber.trim(),
      carSeatsNo: formVehicleFormData.numberOfSeats!,
      citizenship: driverAddParamsCitizenshipType(
        formUserFormData.citizenship!,
      ),
      experience: formUserFormData.yearsOfExperience!,
      idNumber: formUserFormData.personalIdNumber.trim(),
      languages: driverAddParamsLanguagesType(
        formUserFormData.languages.map((lang) => lang),
      ),
      taxiDriverAssociations: formTaxiCorporationData.map((entry) =>
        createDriverAddParamsTaxiDriverAssociation(entry, formUserFormData),
      ),
      addresses: createDriverAddParamsAddresses(formUserFormData.addresses),
    },
  };
};

const railyDriverAddParamsFactory = { createSubmitFormParams };
export default railyDriverAddParamsFactory;
